import { useNavigate } from 'react-router-dom';
import { LuMessageSquare, LuPin, LuLock, LuUser, LuClock } from 'react-icons/lu';
import { format } from 'date-fns';
import { el, enUS } from 'date-fns/locale';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import MentionText from '../common/MentionText';

export default function TopicItem({ topic }) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [authorName, setAuthorName] = useState(t('faultReports.unknownUser'));

  // Get current date locale
  const dateLocale = i18n.language === 'el' ? el : enUS;

  useEffect(() => {
    loadAuthor();
  }, [topic.author_id]);

  const loadAuthor = async () => {
    try {
      const { data, error } = await window.ezsite.apis.tablePage(70648, {
        PageNo: 1,
        PageSize: 1,
        Filters: [{ name: 'id', op: 'Equal', value: topic.author_id }]
      });
      if (!error && data?.List?.[0]) {
        setAuthorName(data.List[0].name);
      }
    } catch (error) {
      console.error('Failed to load author:', error);
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      general: 'bg-gray-100 text-gray-700',
      maintenance: 'bg-orange-100 text-orange-700',
      events: 'bg-purple-100 text-purple-700',
      rules: 'bg-red-100 text-red-700',
      suggestions: 'bg-green-100 text-green-700'
    };
    return colors[category] || colors.general;
  };

  const tags = topic.tags ? topic.tags.split(',').filter((t) => t.trim()) : [];

  return (
    <div
      onClick={() => navigate(`/forum/${topic.id}`)}
      className="p-6 hover:bg-gray-50 cursor-pointer transition-colors">

      <div className="flex items-start gap-4">
        {/* Avatar */}
        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-medium flex-shrink-0">
          {authorName.charAt(0).toUpperCase()}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-4 mb-2">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                {topic.is_pinned && <LuPin className="w-4 h-4 text-blue-600" />}
                {topic.is_locked && <LuLock className="w-4 h-4 text-gray-600" />}
                <h3 className="text-lg font-semibold text-gray-900 hover:text-blue-600">
                  {topic.title}
                </h3>
              </div>
              <div className="flex items-center gap-3 text-sm text-gray-600">
                <span className="flex items-center gap-1">
                  <LuUser className="w-4 h-4" /> {authorName}
                </span>
                <span className="flex items-center gap-1">
                  <LuClock className="w-4 h-4" /> 
                  {format(new Date(topic.created_at), 'MMM d, yyyy', { locale: dateLocale })}
                </span>
              </div>
            </div>
            
            {/* Stats */}
            <div className="flex items-center gap-2 text-gray-600">
              <LuMessageSquare className="w-5 h-5" />
              <span className="font-medium">{topic.reply_count || 0}</span>
            </div>
          </div>

          {/* Body Preview */}
          <div className="text-gray-700 text-sm line-clamp-2 mb-3">
            <MentionText text={topic.body} />
          </div>

          {/* Tags & Category */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${getCategoryColor(topic.category)}`}>
              {t(`forum.categories.${topic.category}`) || topic.category}
            </span>
            {tags.map((tag, idx) =>
            <span key={idx} className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
                #{tag.trim()}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>);

}